import { clsx, type ClassValue } from 'clsx';

// Classname utility
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

// Format file size
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Format duration (seconds to HH:MM:SS or MM:SS)
export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Format relative time
export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  
  return then.toLocaleDateString();
}

// Get file type from extension
export function getFileType(filename: string): 'pdf' | 'docx' | 'pptx' | 'image' | 'audio' | 'video' {
  const ext = filename.split('.').pop()?.toLowerCase();
  
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx'].includes(ext || '')) return 'docx';
  if (['ppt', 'pptx'].includes(ext || '')) return 'pptx';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) return 'image';
  if (['mp3', 'wav', 'm4a', 'ogg'].includes(ext || '')) return 'audio';
  if (['mp4', 'webm', 'mov'].includes(ext || '')) return 'video';
  
  return 'pdf'; // default
}

// Get file icon emoji
export function getFileIcon(type: string): string {
  const icons: Record<string, string> = {
    pdf: '📕',
    docx: '📄',
    pptx: '📊',
    image: '📷',
    audio: '🎤',
    video: '🎬',
  };
  return icons[type] || '📄';
}

// Truncate text
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

// Generate unique ID
export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

// Debounce function
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// Calculate spaced repetition interval
export function calculateNextReview(
  ease: number,
  interval: number,
  quality: 0 | 1 | 2 | 3 // again, hard, good, easy
): { newEase: number; newInterval: number } {
  // Simplified SM-2 algorithm
  let newEase = ease;
  let newInterval = interval;
  
  if (quality === 0) {
    // Again - reset
    newInterval = 1;
    newEase = Math.max(1.3, ease - 0.2);
  } else if (quality === 1) {
    // Hard
    newInterval = Math.max(1, Math.floor(interval * 1.2));
    newEase = Math.max(1.3, ease - 0.15);
  } else if (quality === 2) {
    // Good
    newInterval = Math.floor(interval * ease);
  } else {
    // Easy
    newInterval = Math.floor(interval * ease * 1.3);
    newEase = ease + 0.15;
  }
  
  return { newEase, newInterval };
}
